import { StackLayout } from '@euler/app/components/StackLayout';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { Typography } from '@euler/app/components/typography';
import { ItemMediaList } from '@euler/app/flows/task/components/ItemMediaList';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ConstructionJobProcedureStaged } from '@euler/model/task-detail';
import { memo, useCallback, useMemo } from 'react';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated from 'react-native-reanimated';

const Arrow = memo(
  ({
    type,
    withText,
    style,
    onPress,
  }: {
    type: 'up' | 'down';
    withText?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        onPress={onPress}
        css={`
          flex-direction: row;
          align-items: center;
          margin-left: ${rpx(5)}px;
        `}
        style={style}
      >
        <Icon
          name={type === 'up' ? 'arrow_circle_up' : 'arrow_circle_down'}
          size={rpx(18)}
          color={'#207fe7'}
        />
        {withText && (
          <Typography.Label color="#888" size={rpx(14)}>
            {type === 'up' ? '上移' : '下移'}
          </Typography.Label>
        )}
      </TouchableOpacity>
    );
  },
);

export const ConstructionJobProcedureCell = memo(
  ({
    index,
    editable,
    first,
    last,
    procedure,
    style,
    headerStyle,
    titleStyle,
    titleNumberStyle,
    //animationOrientation,
    onMove,
    onRemove,
    onTakeMedia,
    onDeleteMedia,
    onEditMedia,
    onRetryMediaUpload,
    onPreviewVideo,
  }: {
    first: boolean;
    last: boolean;
    index: number;
    editable?: boolean;
    procedure: ConstructionJobProcedureStaged;
    style?: StyleProp<ViewStyle>;
    headerStyle?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
    titleNumberStyle?: StyleProp<TextStyle>;
    animationOrientation?: 'horizontal' | 'vertical';
    onMove?: (procedure: string, index: number, dir: 'up' | 'down') => void;
    onRemove?: (procedure: string) => void;
    onTakeMedia?: (
      procedure: string,
      allowChooseMediaFromGallery: boolean,
    ) => void;
    onDeleteMedia?: (procedure: string, media: MediaInfo) => void;
    onEditMedia?: (procedure: string, media: MediaInfo) => void;
    onRetryMediaUpload?: (procedure: string, media: MediaInfo) => void;
    onPreviewVideo?: (procedure: string, media: MediaInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const medias = useMemo(
      () => procedure.medias.map(mediaStagedToMediaInfo),
      [procedure.medias],
    );

    const handleTakeMedia = useCallback(() => {
      onTakeMedia?.(
        procedure.name,
        procedure.allowChooseMediaFromGallery !== false,
      );
    }, [onTakeMedia, procedure.allowChooseMediaFromGallery, procedure.name]);

    const handleDeleteMedia = useCallback(
      (media: MediaInfo) => {
        onDeleteMedia?.(procedure.name, media);
      },
      [procedure.name, onDeleteMedia],
    );

    const handleEditMedia = useCallback(
      (media: MediaInfo) => {
        onEditMedia?.(procedure.name, media);
      },
      [onEditMedia, procedure.name],
    );

    const handleRetryMediaUpload = useCallback(
      (media: MediaInfo) => {
        onRetryMediaUpload?.(procedure.name, media);
      },
      [onRetryMediaUpload, procedure.name],
    );

    const handlePreviewVideo = useCallback(
      (media: MediaInfo) => {
        onPreviewVideo?.(procedure.name, media);
      },
      [onPreviewVideo, procedure.name],
    );

    const onMoveUp = useCallback(() => {
      onMove?.(procedure.name, index, 'up');
    }, [index, onMove, procedure.name]);

    const onMoveDown = useCallback(() => {
      onMove?.(procedure.name, index, 'down');
    }, [index, onMove, procedure.name]);

    const handleRemove = useCallback(() => {
      onRemove?.(procedure.name);
    }, [onRemove, procedure.name]);

    const previewImage = usePreviewImage();

    const onStdImagesPress = useCallback(() => {
      const imageUrls = procedure.stdImages
        ?.filter(x => x.type.startsWith('image'))
        .map(x => x.url);
      if (!imageUrls?.length) return;
      previewImage({
        imageUrls,
      });
    }, [previewImage, procedure.stdImages]);

    return (
      <Animated.View style={style}>
        <HStack
          css={`
            padding: ${rpx(10)}px ${rpx(15)}px 0 ${rpx(15)}px;
          `}
          style={headerStyle}
        >
          <HStack
            justifyContent="flex-start"
            css={`
              flex: 1;
            `}
          >
            <Text
              css={`
                font-weight: bold;
                font-style: italic;
                font-size: ${rpx(25)}px;
                color: #207fe7;
                line-height: ${rpx(32)}px;
              `}
              style={titleNumberStyle}
            >
              {index + 1}.{' '}
            </Text>
            <Typography.Label
              medium
              size={rpx(18)}
              color={'#000'}
              lineHeight={rpx(32)}
              style={titleStyle}
            >
              {procedure.name}
            </Typography.Label>
            {procedure.type === 'prod' ? (
              <StackLayout
                direction="row"
                gap={rpx(8)}
                css={`
                  margin-left: ${rpx(8)}px;
                `}
              >
                <Center
                  css={`
                    background-color: ${StatusColors.Notice};
                    border-radius: ${rpx(20)}px;
                    padding-left: ${rpx(8)}px;
                    padding-right: ${rpx(8)}px;
                  `}
                >
                  <Typography.Text
                    color={'#fff'}
                    medium
                    size={rpx(10)}
                    lineHeight={rpx(18)}
                  >
                    推荐
                  </Typography.Text>
                </Center>
                {procedure.stdImages != null &&
                  procedure.stdImages.length > 0 && (
                    <TouchableOpacity onPress={onStdImagesPress}>
                      <Typography.Text
                        medium
                        size={rpx(12)}
                        lineHeight={rpx(18)}
                        letterSpacing={rpx(-1)}
                        opacity={0.5}
                      >
                        参考图 ({procedure.stdImages.length})
                      </Typography.Text>
                    </TouchableOpacity>
                  )}
              </StackLayout>
            ) : null}
          </HStack>
          {editable && (
            <HStack>
              {!first && <Arrow type="up" onPress={onMoveUp} />}
              {!last && <Arrow type="down" onPress={onMoveDown} />}
              <TouchableOpacity
                onPress={handleRemove}
                disabled={procedure.removable === false}
                css={`
                  margin-left: ${rpx(10)}px;
                `}
              >
                <Icon
                  name="minus-circle"
                  color={
                    procedure.removable === false ? '#ccc' : StatusColors.Danger
                  }
                  size={rpx(20)}
                />
              </TouchableOpacity>
            </HStack>
          )}
        </HStack>
        <View
          css={`
            padding: ${rpx(5)}px ${rpx(15)}px ${rpx(15)}px ${rpx(15)}px;
          `}
        >
          <ItemMediaList
            medias={medias}
            editable={editable}
            onTakeMedia={handleTakeMedia}
            onDeleteMedia={handleDeleteMedia}
            onEditMedia={handleEditMedia}
            onPreviewVideo={handlePreviewVideo}
            onRetryMediaUpload={handleRetryMediaUpload}
          />
        </View>
      </Animated.View>
    );
  },
);
